import { createAsyncThunk } from '@reduxjs/toolkit';
import { getStaffSellersRequest, getStaffWebhookHistoryRequest } from './webhookHistoryApi';
import { extractApiError } from '../../utils/errors';

/**
 * The query the backend reads, from the filters as the page keeps them. `all`
 * and empty values are left out: the backend treats a missing filter as "no
 * filter", and a scope only sends the id that belongs to it.
 *
 * @param {object} filters - `state.webhookHistory.filters`
 */
export const buildWebhookHistoryParams = (filters) => {
  const params = { page: filters.page, page_size: filters.rowsPerPage };
  if (filters.scope === 'admin' && filters.adminId && filters.adminId !== 'all') {
    params.admin_id = filters.adminId;
  }
  if (filters.scope === 'seller' && filters.sellerId && filters.sellerId !== 'all') {
    params.seller_id = filters.sellerId;
  }
  if (filters.start_date) params.start_date = filters.start_date;
  if (filters.end_date) params.end_date = filters.end_date;
  if (filters.status && filters.status !== 'all') params.status = filters.status;
  if (filters.substatus && filters.substatus !== 'all') params.substatus = filters.substatus;
  if (filters.dispatch_state && filters.dispatch_state !== 'all') {
    params.dispatch_state = filters.dispatch_state;
  }
  if (filters.search?.trim()) params.search = filters.search.trim();
  return params;
};

export const fetchWebhookHistory = createAsyncThunk(
  'webhookHistory/fetch',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { filters } = getState().webhookHistory;
      const { data } = await getStaffWebhookHistoryRequest(buildWebhookHistoryParams(filters));
      return { results: data.results || [], count: data.count || 0 };
    } catch (error) {
      return rejectWithValue(extractApiError(error, 'No se pudo cargar el historial de webhooks'));
    }
  }
);

/**
 * The sellers of the whole platform, for the "Por seller" filter. Each one
 * carries its operator so the list can be narrowed when an operator is also
 * chosen.
 */
export const fetchWebhookHistorySellers = createAsyncThunk(
  'webhookHistory/fetchSellers',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await getStaffSellersRequest();
      return (data.serialized_sellers || []).map((seller) => ({
        value: seller.id,
        label: seller.company?.social_reason || seller.user?.email || `Seller ${seller.id}`,
        email: seller.user?.email || '',
        adminId: seller.admin ?? null,
      }));
    } catch (error) {
      return rejectWithValue(extractApiError(error, 'No se pudo cargar la lista de sellers'));
    }
  },
  {
    condition: (_, { getState }) => !getState().webhookHistory.sellersLoaded,
  }
);
